import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatchApp } from "../lib/redux";
import { startGetReportedContentPaginated } from "../actions/owner";

const useReportedContent = () => {
  const dispatch = useDispatchApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const lastKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadInitial = async () => {
      setLoading(true);
      setError(null);
      lastKeyRef.current = null;

      const response = await dispatch(
        startGetReportedContentPaginated({
          lastKey: null,
        })
      );

      if (cancelled) return;

      if (!response?.ok) {
        setError(response);
        setRows([]);
        setHasMore(false);
        setLoading(false);
        return;
      }

      const data = response?.data || {};
      setRows(data?.items || []);
      lastKeyRef.current = data?.lastKey || null;
      setHasMore(!!data?.hasMore);
      setLoading(false);
    };

    loadInitial();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    const lastKey = lastKeyRef.current;
    if (!lastKey && rows.length > 0) return;

    setLoadingMore(true);
    setError(null);

    const response = await dispatch(
      startGetReportedContentPaginated({
        lastKey,
      })
    );

    if (!response?.ok) {
      setError(response);
      setHasMore(false);
      setLoadingMore(false);
      return;
    }

    const data = response?.data || {};
    lastKeyRef.current = data?.lastKey || null;
    setRows((prev) => [...prev, ...(data?.items || [])]);
    setHasMore(!!data?.hasMore);
    setLoadingMore(false);
    // eslint-disable-next-line
  }, [dispatch, hasMore, loadingMore, loading]);

  const onUpdateRowStatus = useCallback(({ contentID, status } = {}) => {
    if (!contentID || !status) return;
    setRows((prev) =>
      prev.map((row) =>
        row?.key === contentID
          ? {
              ...row,
              status,
            }
          : row
      )
    );
  }, []);

  return {
    rows,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    onUpdateRowStatus,
  };
};

export default useReportedContent;
